import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import MovieCard from "@/components/feature/MovieCard";
import { useSeo } from "@/hooks/useSeo";
import { movieCategories, regions, years } from "@/mocks/movies";
import { filterMovies } from "@/utils/movie";
import FilterGroup from "@/pages/category/components/FilterGroup";
import Pagination from "@/pages/category/components/Pagination";

const typeOptions = ["全部", "动作", "科幻", "爱情", "悬疑", "剧情", "犯罪", "古装", "都市", "青春", "音乐", "美食", "自然"];
const sortOptions = [
  { key: "update", label: "按更新时间" },
  { key: "hot", label: "按热门程度" },
  { key: "rating", label: "按评分" },
];
const PAGE_SIZE = 8;

export default function Category() {
  const [params, setParams] = useSearchParams();

  const category = params.get("category") || "全部";
  const sub = params.get("sub") || "全部";
  const region = params.get("region") || "全部";
  const year = params.get("year") || "全部";
  const type = params.get("type") || "全部";
  const sort = params.get("sort") || "update";
  const page = Math.max(1, Number(params.get("page")) || 1);

  const currentCategory = movieCategories.find((c) => c.key === category);
  const subOptions = ["全部", ...(currentCategory ? currentCategory.sub : [])];

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === "全部" || (key === "sort" && value === "update")) next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setParams(next, { replace: true });
  };

  const results = useMemo(
    () => filterMovies({ category: category === "全部" ? undefined : category, sub, region, year, type, sort }),
    [category, sub, region, year, type, sort]
  );

  const totalPages = Math.ceil(results.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const paged = results.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const titleText = category === "全部" ? "全部影视作品" : `${category}作品`;
  const seoTitle = `${titleText} - 黑桃视频影视分类`;
  const seoDesc = `黑桃视频影视分类页汇集${titleText}，可按地区、年份与类型筛选并按热度或评分排序，快速找到你想看的影视内容。`;

  useSeo({
    title: seoTitle,
    description: seoDesc,
    keywords: `${titleText},黑桃视频,影视分类,影视筛选,电影,电视剧`,
  });

  const resetFilters = () => setParams(new URLSearchParams(), { replace: true });

  return (
    <Layout>
      <div className="mx-auto w-full max-w-[1440px] px-4 pt-6 md:px-8 md:pt-10">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-500">首页</Link>
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-arrow-right-s-line text-[14px]" aria-hidden="true"></i>
          </span>
          <Link to="/category" className="transition-colors hover:text-primary-500">影视分类</Link>
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-arrow-right-s-line text-[14px]" aria-hidden="true"></i>
          </span>
          <span className="text-foreground-800">{category === "全部" ? "全部" : category}</span>
        </nav>

        <h1 className="mt-5 font-heading text-2xl font-600 tracking-wide text-foreground-950 md:text-3xl">
          {titleText}
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-foreground-600">
          {currentCategory
            ? currentCategory.desc
            : "浏览黑桃视频全部影视内容，按分类、地区、年份与类型自由组合筛选。"}
          当前共 <span className="text-primary-500">{results.length}</span> 部作品。
        </p>

        <div className="mt-6 flex flex-wrap gap-2 border-b border-background-200 pb-3">
          {[{ key: "全部", label: "全部" }, ...movieCategories].map((cat) => {
            const active = category === (cat.key === "全部" ? "全部" : cat.key);
            return (
              <Link
                key={cat.key}
                to={`/category?category=${encodeURIComponent(cat.key)}`}
                className={`whitespace-nowrap rounded-md px-3.5 py-1.5 text-sm transition-colors ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:text-primary-500"
                }`}
              >
                {cat.label}
              </Link>
            );
          })}
        </div>

        {currentCategory && (
          <div className="mt-5">
            <FilterGroup
              label="子分类"
              options={subOptions}
              value={sub}
              onChange={(v) => updateParam("sub", v)}
            />
          </div>
        )}

        <div className="mt-5 space-y-3 rounded-lg border border-background-200 bg-background-100 p-4">
          <FilterGroup label="地区" options={regions} value={region} onChange={(v) => updateParam("region", v)} />
          <FilterGroup label="年份" options={years} value={year} onChange={(v) => updateParam("year", v)} />
          <FilterGroup label="类型" options={typeOptions} value={type} onChange={(v) => updateParam("type", v)} />
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm text-foreground-600">
            共 <span className="text-foreground-900">{results.length}</span> 条结果
          </span>
          <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
            {sortOptions.map((opt) => {
              const active = sort === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => updateParam("sort", opt.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                    active
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:text-primary-400"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {paged.length === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center text-foreground-400">
              <i className="ri-inbox-2-line text-[40px]" aria-hidden="true"></i>
            </span>
            <p className="mt-3 text-sm text-foreground-600">无匹配影视作品</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-4">
            {paged.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        )}

        <Pagination page={safePage} totalPages={totalPages} onChange={(p) => updateParam("page", String(p))} />
      </div>
    </Layout>
  );
}